import React from "react";
import cx from "classnames";
import { invoiceData, expenseData } from "../../@types";
import { useNavigate } from "react-router-dom";
import { formatMoney, useGetPaymentCurrencyList } from "../../utils";
import { useTranslation } from "react-i18next";
import { EmptyPieChart } from "./PieChart/Empty";
import { useCompanyStore } from "../../state/useCompanyStore";
import InvoiceEmptyStats from "../../assets/icons/InvoiceEmptyStats";
import InvoiceLoader from "./Loading/InvoiceLoader";
interface InvoiceCardProps {
  title: string;
  value: string;
  text: string;
  icon: React.ReactNode;
  totalNumber: number | null;
  refundNumber: number | null;
  data?: invoiceData | expenseData | any;
  toolTipText?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isLoading: boolean;
  isFilter: boolean;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  title,
  data,
  value,
  icon,
  startDate,
  endDate,
  refundNumber,
  isLoading,
  isFilter,
}) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const isTrue =
    value?.trim()?.toLowerCase() === "filter by date"?.toLowerCase();

  const isEmpty =
    data?.totalInvoiceValue == 0 &&
    data?.totalPaidInvoiceValue == 0 &&
    data?.totalUnPaidInvoiceValue == 0;

  const emptyComponent = () => {
    return (
      <div className="pb-2 pt-0 bg-white rounded-lg w-full">
        <EmptyPieChart
          link={`/invoices/create`}
          buttonText="Create an invoice"
          icon={<InvoiceEmptyStats />}
          paragraph={
            <>
              Create your invoice to start <br /> recording payments.
            </>
          }
          title="Invoices"
        />
      </div>
    );
  };

  return (
    <>
      {isLoading ? (
        <InvoiceLoader />
      ) : (
        <>
          <div className=" pt-0 bg-white flex flex-col h-full rounded-lg w-full">
            <div className="flex-1">
              <div className="pt-5 pb-2">
                <h2 className="px-3 md:px-6 text-[#1D2939] text-[16px] font-medium leading-6">
                  {title}
                </h2>
              </div>
              <hr className="border border-gray-200 w-full my-2" />
              <div className="px-3 py-2 grid md:grid-cols-2 grid-cols-1 gap-2">
                <Card
                  title={t("totalValue")}
                  bgColorClass="bg-[#ECFDF3] text-[#05603A] h-[100px] md:h-[130px]"
                  value={data?.totalInvoiceValue ?? 0}
                />
                <Card
                  title={"Refunded"}
                  bgColorClass="bg-[#FFFAEB] text-[#B54708] h-[100px] md:h-[130px]"
                  value={`${refundNumber ?? 0}`}
                />

                <Card
                  title={t("invoicePaid")}
                  bgColorClass="bg-[#E0EEFE] text-[#0D3C6D] h-[100px] md:h-[130px]"
                  value={data?.totalPaidInvoiceValue ?? 0}
                />
                <Card
                  title={t("invoiceUnpaid")}
                  bgColorClass="bg-[#FEE4E2] text-[#B42318] h-[100px] md:h-[130px]"
                  value={data?.totalUnPaidInvoiceValue ?? 0}
                />
              </div>
              <div className="flex justify-between mx-auto w-full items-center mt-4 px-2">
                <button
                  onClick={() => {
                    if (data?.totalInvoiceValue) {
                      navigate(
                        `/invoices?startDate=${startDate}&endDate=${endDate}&selectedLabel=${value}`,
                      );
                    } else {
                      navigate(`/invoices/create`);
                    }
                  }}
                  className="border-[#044E97] mx-auto transition-all duration-500 hover:bg-[#044E97] hover:text-[#ffffff] p-[8px] border rounded-md text-[14px] font-medium text-[#044E97] "
                >
                  {data?.totalInvoiceValue ? "View Invoice" : "Create Invoice"}
                </button>
              </div>
            </div>
            <div className="flex w-full bg-[#F9FAFB] justify-center items-center rounded-b-md border-t border-t-[#EAECF0] mt-3">
              <h6 className="w-full text-center text-[#667085] text-[14px] font-medium pt-[15px] pb-[15px]">
                {isTrue ? "Today" : value}
              </h6>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { InvoiceCard };

interface InfoCardProps {
  title: string;
  value: string;
  bgColorClass: string;
  valuePosition?: { marginRight: string };
}

const Card: React.FC<InfoCardProps> = ({
  title,
  value,
  bgColorClass,
  valuePosition,
}) => {
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const bgClassName = (bgColorClass: string) => {
    return cx("w-full  rounded-lg flex items-center justify-center py-5", {
      [bgColorClass]: true,
    });
  };

  return (
    <div className={`${bgClassName(bgColorClass)}`}>
      <div className="font-semibold text-center">
        <p className="text-[14px] leading-4">{title}</p>
        <p className={"text-[16px] leading-6 mt-3"} style={valuePosition}>
          {formatMoney(value, userCurrencyCode?.currency ?? "NGN")}
        </p>
      </div>
    </div>
  );
};

export { Card };
